import { useState } from 'react';
import { SEASON_2025_RECAP, type Manager2025Finish } from '../constants/season2025Recap';
import { TeamLogo } from './TeamLogo';

interface Props {
  onClose: () => void;
}

export function Season2025RecapModal({ onClose }: Props) {
  const [selectedManager, setSelectedManager] = useState<Manager2025Finish>(SEASON_2025_RECAP.standings[0]);
  const [activeTab, setActiveTab] = useState<'standings' | 'bargains' | 'takeaways'>('standings');

  return (
    <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-3 md:p-6 z-50">
      <div className="bg-slate-900 border-2 border-amber-500/80 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl shadow-amber-950/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 md:px-6 bg-gradient-to-r from-amber-950/90 via-slate-900 to-amber-950/90 border-b border-amber-500/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🏆</span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-black text-white">
                  2025 NFL Fantasy Auction Season Recap
                </h2>
                <span className="pill bg-amber-500 text-slate-950 font-black text-[10px] uppercase">
                  OFFICIAL ARCHIVE
                </span>
              </div>
              <p className="text-xs text-amber-200/80">
                David crowned Champion (71 Wins) · Rob Runner-Up (69 Wins) · Ryan 3rd (68 Wins) · Frank 4th (64 Wins)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 px-2.5 rounded-lg bg-slate-800 hover:bg-rose-900 text-slate-400 hover:text-white transition text-sm font-bold"
          >
            ✕
          </button>
        </div>

        {/* Champion Spotlight Banner */}
        <div className="p-4 px-6 bg-gradient-to-r from-amber-500/10 via-amber-500/20 to-amber-500/10 border-b border-amber-500/30 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center text-3xl shadow-lg shadow-amber-500/20">
              👑
            </div>
            <div>
              <div className="text-[10px] uppercase font-black tracking-widest text-amber-400">
                2025 LEAGUE CHAMPION
              </div>
              <div className="text-2xl font-black text-white flex items-center gap-2">
                <span>David</span>
                <span className="text-sm font-extrabold text-amber-300">71-65 (.522 Win%)</span>
              </div>
              <p className="text-xs text-slate-300 max-w-xl mt-0.5">
                {SEASON_2025_RECAP.champion.summary}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="bg-black/40 px-3 py-2 rounded-xl border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">Winning Wins</span>
              <span className="text-lg font-black text-emerald-400">71 Wins</span>
            </div>
            <div className="bg-black/40 px-3 py-2 rounded-xl border border-white/5 text-center">
              <span className="text-[10px] text-slate-400 uppercase font-bold block">Margin of Victory</span>
              <span className="text-lg font-black text-amber-400">+2 Wins over Rob</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-800 px-6 bg-slate-900/60">
          <button
            onClick={() => setActiveTab('standings')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'standings'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            📊 Final Standings & Rosters
          </button>
          <button
            onClick={() => setActiveTab('bargains')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'bargains'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            💎 Best Bargains of 2025
          </button>
          <button
            onClick={() => setActiveTab('takeaways')}
            className={`py-3 px-4 text-xs font-bold border-b-2 transition ${
              activeTab === 'takeaways'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            💡 2026 Strategy Takeaways
          </button>
        </div>

        {/* Body Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
          {/* TAB 1: STANDINGS & ROSTERS */}
          {activeTab === 'standings' && (
            <div className="space-y-4">
              {/* Standings Grid Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {SEASON_2025_RECAP.standings.map(m => {
                  const isSelected = selectedManager.id === m.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => setSelectedManager(m)}
                      className={`p-3 rounded-xl border text-left transition flex flex-col justify-between ${
                        isSelected
                          ? 'bg-amber-950/40 border-amber-400 shadow-md shadow-amber-500/10'
                          : 'bg-slate-800/60 border-slate-700 hover:bg-slate-800'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xl">{m.emoji}</span>
                          <span className="text-[10px] font-black text-amber-400">{m.badge}</span>
                        </div>
                        <div className="text-base font-black text-white">{m.name}</div>
                        <div className="text-xs font-extrabold text-emerald-400 mt-0.5">
                          {m.wins} - {m.losses} <span className="text-slate-400 font-normal">({(m.winPct * 100).toFixed(1)}%)</span>
                        </div>
                      </div>

                      <div className="mt-3 pt-2 border-t border-white/5 text-[11px] text-slate-400 flex justify-between">
                        <span>Spent: ${m.budgetSpent}</span>
                        <span>${m.costPerWin.toFixed(2)}/win</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Selected Manager Roster Breakdown */}
              <div className="bg-slate-800/70 rounded-xl p-4 border border-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{selectedManager.emoji}</span>
                    <div>
                      <h3 className="text-base font-black text-white">
                        {selectedManager.name}'s 2025 Roster Breakdown
                      </h3>
                      <p className="text-xs text-slate-400">
                        {selectedManager.keyStoryline}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs font-semibold text-amber-300">
                    Total: <strong className="text-white">{selectedManager.wins} Wins</strong> · Budget Spent: ${selectedManager.budgetSpent}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
                  {selectedManager.teams.map(t => (
                    <div
                      key={t.abbr}
                      className="bg-black/30 p-2.5 rounded-lg border border-white/5 flex items-center gap-2.5"
                    >
                      <TeamLogo abbr={t.abbr} size={36} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-extrabold text-xs text-white truncate">{t.name}</span>
                          <span className="text-[11px] font-bold text-emerald-400">${t.price}</span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-400 mt-0.5">
                          <span>Record: <strong className="text-white">{t.record}</strong></span>
                          <span>${t.costPerWin.toFixed(2)}/W</span>
                        </div>
                        <div className="text-[9px] text-cyan-300 font-semibold truncate mt-0.5">
                          {t.verdict}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: BEST BARGAINS OF 2025 */}
          {activeTab === 'bargains' && (
            <div className="space-y-3">
              <p className="text-xs text-slate-400">
                The most profitable draft values that defined the 2025 championship race:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {SEASON_2025_RECAP.topBargains.map((b, i) => (
                  <div
                    key={b.team}
                    className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700 flex items-start gap-3"
                  >
                    <TeamLogo abbr={b.team} size={42} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="font-black text-sm text-white truncate">
                          #{i + 1} {b.teamName} ({b.team})
                        </span>
                        <span className="pill bg-emerald-950 border border-emerald-500/50 text-emerald-300 text-[10px] font-extrabold">
                          ${b.costPerWin.toFixed(2)}/Win
                        </span>
                      </div>
                      <div className="text-xs font-semibold text-slate-300 mb-1">
                        Drafted by <strong className="text-white">{b.manager}</strong> for{' '}
                        <strong className="text-emerald-400">${b.price}</strong> · Generated{' '}
                        <strong className="text-amber-300">{b.wins} Wins</strong>
                      </div>
                      <p className="text-[11px] text-slate-400 italic">
                        "{b.highlight}"
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 3: STRATEGIC LESSONS FOR 2026 */}
          {activeTab === 'takeaways' && (
            <div className="space-y-4">
              <div className="bg-slate-800/60 p-4 rounded-xl border border-cyan-500/30">
                <h3 className="text-sm font-extrabold text-cyan-300 mb-3 flex items-center gap-2">
                  <span>🧠</span>
                  <span>Key Game-Theory Takeaways for Rob's 2026 Draft</span>
                </h3>
                <div className="space-y-2.5">
                  {SEASON_2025_RECAP.notableTakeaways.map((takeaway, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-slate-200">
                      <span className="text-amber-400 font-bold">•</span>
                      <span>{takeaway}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
